// ═══════════════════════════════════════════════════════════════════════════════
// Drain offline queue when online (upload blobs → API)
// ═══════════════════════════════════════════════════════════════════════════════

import { receiptApi, taskInstanceApi } from '../lib/api';
import { uploadTaskPhotoViaBackend, uploadReceiptPhotoViaBackend } from '../lib/directPhotoUpload';
import { dataUrlToBlob } from './dataUrl';
import {
  getOfflineQueue,
  removeQueueItemById,
} from './offlineQueue';
import type { OfflineQueueItem } from './offlineQueueTypes';

const resolveTaskPhotoUrl = async (url: string): Promise<string> => {
  if (!url.startsWith('data:')) {
    return url;
  }
  const blob = dataUrlToBlob(url);
  const fileName = 'offline-photo.jpg';
  return uploadTaskPhotoViaBackend(blob, fileName);
};

const resolveReceiptImageUrl = async (url: string): Promise<string> => {
  if (!url.startsWith('data:')) {
    return url;
  }
  const blob = dataUrlToBlob(url);
  const fileName = 'offline-receipt.jpg';
  return uploadReceiptPhotoViaBackend(blob, fileName);
};

const processOne = async (item: OfflineQueueItem): Promise<void> => {
  if (item.kind === 'task_submit') {
    const photoUrls = await Promise.all(item.payload.photoUrls.map(resolveTaskPhotoUrl));
    const res = await taskInstanceApi.submit(item.taskInstanceId, {
      ...item.payload,
      photoUrls,
    });
    if (!res.success) {
      throw new Error(res.error ?? 'Task submit failed');
    }
    return;
  }

  if (item.kind === 'task_resubmit') {
    const photoUrls = await Promise.all(item.payload.photoUrls.map(resolveTaskPhotoUrl));
    const res = await taskInstanceApi.resubmit(item.taskInstanceId, {
      ...item.payload,
      photoUrls,
    });
    if (!res.success) {
      throw new Error(res.error ?? 'Task resubmit failed');
    }
    return;
  }

  const originalImageUrl = await resolveReceiptImageUrl(item.originalImageUrlOrData);
  const res = await receiptApi.create({
    ...item.payload,
    originalImageUrl,
  });
  if (!res.success) {
    throw new Error(res.error ?? 'Receipt create failed');
  }
};

export type ProcessQueueResult = {
  processed: number;
  failedId: string | null;
  failedMessage: string | null;
};

export const processOfflineQueue = async (): Promise<ProcessQueueResult> => {
  if (!navigator.onLine) {
    return { processed: 0, failedId: null, failedMessage: null };
  }

  const queue = await getOfflineQueue();
  const pending = [...queue].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  let processed = 0;
  for (const item of pending) {
    try {
      await processOne(item);
      await removeQueueItemById(item.id);
      processed += 1;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Sync failed';
      return { processed, failedId: item.id, failedMessage: msg };
    }
  }

  return { processed, failedId: null, failedMessage: null };
};
